import { Box, Chip, IconButton, SelectChangeEvent, Typography } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CalendarIcon from '../../icons/CalendarIcon.svg';
import useMediaQuery from '@mui/material/useMediaQuery';
import CalendarModalV2, { IsoDateRange } from './CalendarModal.V2';
import SearchInputV2 from './SearchInput.V2';
import DropDownFilter, { DropDownItem } from './DropDownFilter';
import { format, parseISO } from 'date-fns';

interface SearchableTableHeaderPropTypes {
  /**
   * Placeholder/watermark text to display in the box when no value is entered.
   */
  searchValuePlaceholder?: string;

  /**
   * Bit flag to hide the open calendar icon. defaults to false.
   */
  hideCalendarIcon?: boolean;

  /**
   * Bit flag to hide the search input field. Defaults to false.
   */
  hideSearchInput?: boolean;

  /**
   * List of values to show in the droplist.
   * TODO: make this optional so people can use this just for the date portion
   */
  dropDownOptionValues?: DropDownItem[];

  /**
   * Optional callback function. Executes when the dropdown value is changed.
   * @param value the `value` property of the selected item of the `dropDownOptionValues`
   * @returns
   */
  dropDownValueChangeHandler?: (value: string) => void;

  /**
   * Optional callback function when the search value is changed.
   * @param value string value of the search text as it changes
   * @returns
   */
  searchValueChangeHandler?: (value: string) => void;

  /**
   * Optional callback for when the date chip is closed.
   * @returns
   */
  dateChipCloseHandler?: () => void;

  /**
   * Optional callback for when the search chip is closed.
   * @returns
   */
  searchValueChipCloseHandler?: () => void;

  /**
   * Optional initial date range selection for the calendar.
   */
  initialDateRange?: IsoDateRange;

  /**
   * Bit flag to know whether to return the full iso date+time or just the date portion.
   * @default false
   */
  isDateOnly?: boolean;

  /**
   * Dropdown list value. Needs to be one of the `key` entries of `dropDownOptionValues`.
   */
  searchParam?: string;

  /**
   * Optional initial value to put in the search box.
   */
  initialSearchValue?: string;

  /**
   * Optional callback that gets fired when the user selects continue on the calendar modal.
   * This event fires prior to the `executeSearch`, but no guranty that react will treat them in order.
   * This can be used if there is custom logic desired when the dates are selected (albeit, you can also use the `executeSearch` event).
   * @param val DateRange<Date>
   * @returns
   */
  calendarDatesSelectedEvent?: (val: IsoDateRange) => void;

  /**
   * What's the point of this? I don't see how this is used.
   */
  hideCalendarModal?: boolean;

  /**
   * This is the function that will be invoked when one of the following events occur:
   * - Spyglass clicked on search box
   * - Enter key hit in search box
   * - Date selected
   * - Any chip is closed.
   * @param searchField Optional param. This is the name of the search field (drop down item)
   * @param searchValue Optional param. This will be the value in the textbox
   * @param dateRange Optional param. This will be the value of the date range
   * @returns `void`
   */
  executeSearch: (searchField?: string, searchValue?: string, dateRange?: IsoDateRange) => void;

  /**
   * !I don't know what this does. Figure that out and delete if not needed
   */
  columnsSelectRef?: React.RefObject<HTMLSelectElement>;

  /**
   * !I don't know what this does. Figure that out and delete if not needed
   */
  searchValueInputRef?: React.RefObject<HTMLInputElement>;
}

/**
 * Inosio Generic Table Header. Keep this generic. Do not tightly coulple with any consumer.
 * @param props
 * @returns
 */
const SearchableTableHeader = (props: SearchableTableHeaderPropTypes) => {
  const matchesMobile = useMediaQuery('(max-width:420px)');
  const matchesIpad = useMediaQuery('(max-width:768px)');

  const [errorMsg, setErrorMsg] = useState('');
  const [searchValueText, setSearchValueText] = useState(props.initialSearchValue || '');
  const [selectedDropDownValue, setSelectedDropDownValue] = useState<string | undefined>(props.searchParam);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isSearchTextChipOpen, setIsSearchTextChipOpen] = useState(false);
  const [searchTextChipValue, setSearchTextChipValue] = useState(''); //we track this independent of what they are actively typing so this only changes when they actually invoke a search
  const [dateChipTextValue, setDateChipTextValue] = useState(''); //we track this independent of what they are actively typing so this only changes when they actually invoke a search
  const [isDateChipOpen, setIsDateChipOpen] = useState(false);
  const [calendarWidgetDateRange, setCalendarWidgetDateRange] = useState<IsoDateRange>(props.initialDateRange || [null, null]);

  useEffect(() => {
    if (props.initialSearchValue) {
      const ddlDisplayText = props.dropDownOptionValues?.find((d) => d.key === selectedDropDownValue)?.value;

      setSearchTextChipValue(`${ddlDisplayText} : ${searchValueText}`);
      setIsSearchTextChipOpen(true);
    }

    if (props.initialDateRange) {
      setIsDateChipOpen(true);
      const beginDateChip = props.initialDateRange[0] ? format(parseISO(props.initialDateRange[0]), 'yyyy-MM-dd') : '';
      const endDateChip = props.initialDateRange[1] ? format(parseISO(props.initialDateRange[1]), 'yyyy-MM-dd') : '';

      setDateChipTextValue(`${beginDateChip} - ${endDateChip}`);
    }
  }, []);

  const handleSearch = (searchDropDownValue?: string, searchValueText?: string, searchDateRange?: IsoDateRange) => {
    //error case is when they have search text but no search field dropdown.
    if (!!searchValueText?.length && !searchDropDownValue) {
      setErrorMsg('Please select a filter');
      return;
    }

    if (!!searchValueText?.length && !!searchDropDownValue) {
      setIsSearchTextChipOpen(true);
    }

    setErrorMsg('');
    const trimmedText = searchValueText?.trim();
    setSearchValueText(trimmedText || '');
    props.executeSearch(searchDropDownValue, trimmedText, searchDateRange);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          p: 0.5,
          pb: matchesIpad ? 0 : 2,
          flexDirection: matchesIpad ? 'column' : 'row',
          justifyContent: 'end',
          alignItems: matchesIpad ? 'baseline' : 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box style={{ marginRight: '13px' }}>
            {props.dropDownOptionValues?.length && (
              <DropDownFilter
                searchFieldChangeHandler={(e: SelectChangeEvent<any>, child: ReactNode) => {
                  setSelectedDropDownValue(e.target.value);
                  if (props?.dropDownValueChangeHandler) props.dropDownValueChangeHandler(e.target.value);
                }}
                selectedValue={selectedDropDownValue}
                optionValues={props.dropDownOptionValues}
                columnsSelectRef={props.columnsSelectRef}
              />
            )}
          </Box>
          <Box style={{ marginRight: matchesMobile ? '10px' : '20px' }}>
            {!props?.hideSearchInput && (
              <SearchInputV2
                handleSearch={() => {
                  const ddlDisplayText = props.dropDownOptionValues?.find((d) => d.key === selectedDropDownValue)?.value;
                  setSearchTextChipValue(`${ddlDisplayText} : ${searchValueText}`);
                  handleSearch(selectedDropDownValue, searchValueText, calendarWidgetDateRange);
                }}
                searchValueInputRef={props.searchValueInputRef}
                searchValueChangeHandler={(e) => {
                  setSearchValueText(e.target.value);
                  if (props.searchValueChangeHandler) props.searchValueChangeHandler(e.target.value);
                }}
                searchValue={searchValueText}
                disableSearchIcon={!selectedDropDownValue && !searchValueText}
                isSearchValueEmpty={!searchValueText}
                placeholder={props.searchValuePlaceholder}
                width={matchesMobile ? '130px' : matchesIpad ? '200px' : undefined}
              />
            )}
          </Box>
          {!props?.hideCalendarIcon && (
            <Box style={{ marginRight: '24px' }}>
              <IconButton
                aria-label="calendar"
                onClick={() => {
                  setIsCalendarModalOpen(!isCalendarModalOpen);
                }}
              >
                <img src={CalendarIcon} alt="calendar icon" width="24px" height="24px" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: matchesIpad ? 1.5 : 0 }}>
          {isSearchTextChipOpen && (
            <Box style={{ marginRight: '13px' }}>
              <Chip
                label={searchTextChipValue}
                sx={{
                  border: '2px #39A2DB solid',
                  bgcolor: '#ffffff',
                  color: '#39A2DB',
                  height: '42px',
                  fontSize: '18px',
                  lineHeight: '22px',
                  borderRadius: '24px',
                }}
                onDelete={(event: any) => {
                  setIsSearchTextChipOpen(false);
                  setSelectedDropDownValue(props.searchParam);
                  setSearchValueText('');
                  if (props.searchValueChipCloseHandler) props.searchValueChipCloseHandler();
                  handleSearch(undefined, undefined, calendarWidgetDateRange);
                }}
                deleteIcon={<CloseIcon sx={{ color: '#39A2DB !important' }} />}
              />
            </Box>
          )}

          {isDateChipOpen && (
            <Chip
              label={dateChipTextValue}
              sx={{
                border: '2px #39A2DB solid',
                bgcolor: '#ffffff',
                color: '#39A2DB',
                height: '42px',
                fontSize: '18px',
                lineHeight: '22px',
                borderRadius: '24px',
              }}
              onDelete={(event: any) => {
                setIsDateChipOpen(false);
                if (props.dateChipCloseHandler) props.dateChipCloseHandler();
                setCalendarWidgetDateRange([null, null]);
                handleSearch(selectedDropDownValue, searchValueText);
              }}
              deleteIcon={<CloseIcon sx={{ color: '#39A2DB !important' }} />}
            />
          )}
        </Box>

        {!props?.hideCalendarModal && (
          <CalendarModalV2
            isDateOnly={props.isDateOnly || false}
            handleClose={(val: any) => {
              setIsCalendarModalOpen(false);
            }}
            initialDateRange={props.initialDateRange || [null, null]}
            isOpen={isCalendarModalOpen}
            handleCalendarSubmit={(selectedDateRange: IsoDateRange) => {
              setCalendarWidgetDateRange(selectedDateRange);
              if (props.calendarDatesSelectedEvent) props.calendarDatesSelectedEvent(selectedDateRange);
              setIsCalendarModalOpen(false);
              setIsDateChipOpen(true);
              const beginDateChip = selectedDateRange[0] ? format(parseISO(selectedDateRange[0]), 'yyyy-MM-dd') : '';
              const endDateChip = selectedDateRange[1] ? format(parseISO(selectedDateRange[1]), 'yyyy-MM-dd') : '';

              setDateChipTextValue(`${beginDateChip} - ${endDateChip}`);
              handleSearch(selectedDropDownValue, searchValueText, selectedDateRange);
            }}
          />
        )}
      </Box>
      {errorMsg.length !== 0 && (
        <Typography
          sx={{
            fontWeight: 400,
            fontFamily: 'Work Sans',
            fontSize: '14px',
            lineHeight: '18px',
            color: '#d32f2f',
            marginLeft: '18px',
            marginTop: '-5px',
          }}
        >
          {errorMsg}
        </Typography>
      )}
    </Box>
  );
};

export default SearchableTableHeader;
